import { z } from 'zod/v4';
import { FieldChangeSchema } from './field-change.js';

/** The actor block of an entry — who did it, as the request context knew them. */
export const EntityHistoryActorSchema = z.object({
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  companyId: z.string().nullable(),
  companyName: z.string().nullable(),
  impersonateId: z.string().nullable(),
  impersonateName: z.string().nullable(),
  apiKeyId: z.string().nullable(),
  apiKeyLabel: z.string().nullable(),
});

export type EntityHistoryActor = z.infer<typeof EntityHistoryActorSchema>;

/**
 * One `entity_history` row as the API returns it. `metadata` is left generic
 * here; a route for one entity type narrows it with the matching member of
 * `EntityHistoryMetadataSchema` (see `buildEntityHistoryEntrySchema`).
 */
export const EntityHistoryEntrySchema = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  entitySubType: z.string().nullable(),
  eventType: z.string(),
  source: z.string(),
  correlationId: z.string().nullable(),
  forCompanyId: z.string(),
  byCompanyId: z.string(),
  onBehalfOfCompanyId: z.string(),
  changes: z.array(FieldChangeSchema).nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  createdBy: EntityHistoryActorSchema,
});

export type EntityHistoryEntry = z.infer<typeof EntityHistoryEntrySchema>;

/** The entry schema with `metadata` narrowed to one entity type's shape. */
export const buildEntityHistoryEntrySchema = <T extends z.ZodTypeAny>(
  metadata: T,
) => EntityHistoryEntrySchema.extend({ metadata });
